import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import React from 'react';
import { Card } from '@components/atoms/Card';
import { ProjectStatusBadge } from '@components/atoms/ProjectStatusBadge';
import { Project } from '@models/project.model';
import { ClientProjectOverview } from '@models/client.model';
import { useTranslation } from '@hooks/useTranslation';
import { ClientProgressStrip } from './ClientProgressStrip';
import { fillTemplate, formatClientProjectDates } from './clientProjectDisplay';

interface ClientProjectCardProps {
  project: Project;
  onClick: (projectId: string) => void;
  /** Sprint 44: the server's progress row for this project; omitted when unavailable. */
  overview?: ClientProjectOverview;
  overviewLoading?: boolean;
}

/**
 * Sprint 43, story-223. One project on the CLIENT home. Looks like the superintendent rail card
 * (same Card, image, typography) but is a real button with a focus ring, shows a dates line, and
 * carries no star toggle and no geofence control: a client cannot write, and never sees geofences.
 */
export const ClientProjectCard: React.FC<ClientProjectCardProps> = ({
  project,
  onClick,
  overview,
  overviewLoading = false,
}) => {
  const tDashboard = useTranslation('dashboard');
  const dates = formatClientProjectDates(project, {
    range: tDashboard.clientProjectDates,
    starts: tDashboard.clientProjectStarts,
    none: tDashboard.clientProjectNoDates,
  });

  const open = () => {
    if (project.projectId) onClick(project.projectId);
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-label={fillTemplate(tDashboard.clientOpenProject, {
        name: project.name,
      })}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400">
      <div className="flex gap-3 items-center">
        {project.siteImage ? (
          <img
            className="w-10 h-10 object-cover rounded-md flex-shrink-0"
            src={project.siteImage}
            alt=""
          />
        ) : (
          <div className="w-10 h-10 bg-gray-200 text-gray-500 dark:bg-gray-600 rounded-md flex items-center justify-center flex-shrink-0">
            <ImageOutlinedIcon sx={{ fontSize: '18px' }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate">{project.name}</div>
          {project.address && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {project.address}
            </div>
          )}
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {dates}
          </div>
        </div>
        {project.state && (
          <ProjectStatusBadge
            state={project.state}
            isBehind={project.isBehind}
            size="sm"
          />
        )}
      </div>
      <ClientProgressStrip overview={overview} isLoading={overviewLoading} />
    </Card>
  );
};
